import { Link, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ArrowRight, Sparkles, Trophy, LineChart, ShieldCheck, BellRing } from "lucide-react";

export default function Landing() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-white">
      <header className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <img
            src="/icons/icon-192.png"
            alt="TradePilot"
            className="w-9 h-9 rounded-lg shadow-sm ring-1 ring-brand/10"
          />
          <span className="font-display font-semibold text-lg tracking-tight text-brand-ink">
            Trade<span className="text-brand">Pilot</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Link
            to="/login"
            data-testid="landing-login-btn"
            className="text-sm font-medium text-gray-600 hover:text-brand-deep px-4 py-2"
          >
            Sign in
          </Link>
          <Link
            to="/register"
            data-testid="landing-register-btn"
            className="text-sm font-medium bg-brand-gradient text-white rounded-full px-5 py-2 shadow-sm hover:opacity-90 transition-transform active:scale-95"
          >
            Get started
          </Link>
        </div>
      </header>

      <section className="max-w-7xl mx-auto px-6 pt-20 pb-24 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <p className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.1em] text-gray-500 mb-6">
            <Sparkles size={12} /> Paper trading · AI graded
          </p>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-medium tracking-tight leading-[1.05] text-brand-ink">
            Trade fearlessly.<br />
            <span className="text-brand-gradient">Learn ruthlessly.</span>
          </h1>
          <p className="mt-6 text-lg text-gray-600 max-w-xl leading-relaxed">
            Practice stocks and crypto with $100,000 in paper money. Every trade gets
            graded A-F by Claude with actionable coaching. Climb the leaderboard,
            follow top traders, and build a real edge — risk-free.
          </p>
          <div className="mt-10 flex items-center gap-4">
            <Link
              to="/register"
              data-testid="hero-cta-register"
              className="inline-flex items-center gap-2 bg-brand-gradient text-white rounded-full px-8 py-4 font-medium shadow-md hover:opacity-90 transition-transform active:scale-95"
            >
              Start trading free <ArrowRight size={18} />
            </Link>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-brand-deep font-medium px-4 py-4"
            >
              I have an account
            </Link>
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="relative rounded-3xl border border-gray-200 bg-white p-6 shadow-[0_20px_60px_-20px_rgba(0,0,0,0.15)]">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.1em] text-gray-500">Portfolio</p>
                <p className="font-display text-4xl font-medium tabular-nums mt-1">$127,412.88</p>
                <p className="text-sm font-medium text-gain tabular-nums mt-1">+$27,412.88 (+27.41%)</p>
              </div>
              <div className="w-16 h-16 rounded-2xl bg-gain-soft flex items-center justify-center">
                <LineChart className="text-gain" size={28} />
              </div>
            </div>
            <div className="h-32 rounded-2xl bg-gradient-to-b from-white to-gray-50 border border-gray-100 relative overflow-hidden">
              <svg viewBox="0 0 400 120" className="w-full h-full">
                <path
                  d="M0,90 C 40,70 60,80 90,60 C 120,40 150,55 180,45 C 210,35 240,20 280,25 C 320,30 350,15 400,10"
                  stroke="#16a34a"
                  strokeWidth="2.5"
                  fill="none"
                />
              </svg>
            </div>
            <div className="grid grid-cols-3 gap-3 mt-4">
              {[
                { label: "BTC", change: "+3.4%", up: true },
                { label: "AAPL", change: "-0.8%", up: false },
                { label: "TSLA", change: "+1.2%", up: true },
              ].map((c) => (
                <div key={c.label} className="rounded-xl border border-gray-200 p-3">
                  <p className="text-xs font-bold text-gray-500">{c.label}</p>
                  <p className={`text-sm font-mono ${c.up ? "text-gain" : "text-loss"}`}>{c.change}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24 grid md:grid-cols-3 gap-6">
        {[
          {
            icon: Sparkles,
            title: "AI grading",
            body: "Claude Sonnet 4.5 grades every trade A-F with concrete coaching on entry, sizing, and risk.",
          },
          {
            icon: Trophy,
            title: "Global leaderboard",
            body: "Compete on PnL, win rate, and consistency. Real fame, no capital at risk.",
          },
          {
            icon: BellRing,
            title: "Price alerts",
            body: "Set price triggers on crypto and stocks. We watch the tape so you don't have to.",
          },
        ].map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-2xl border border-gray-200 p-6 hover:border-brand/40 hover:shadow-sm transition-all">
            <div className="w-10 h-10 rounded-xl bg-brand-tint flex items-center justify-center">
              <Icon className="text-brand-deep" size={20} />
            </div>
            <h3 className="font-display text-xl mt-4 font-medium">{title}</h3>
            <p className="text-sm text-gray-600 mt-2 leading-relaxed">{body}</p>
          </div>
        ))}
      </section>
    </div>
  );
}
